import { Box, Text, VStack } from "@chakra-ui/react"

const EPC_ARROW_SHAPE_RIGHT =
  "polygon(0 0, calc(100% - 18px) 0, 100% 50%, calc(100% - 18px) 100%, 0 100%)"
const EPC_ARROW_SHAPE_LEFT =
  "polygon(18px 0, 100% 0, 100% 100%, 18px 100%, 0 50%)"

const EPC_SCALE = [
  {
    label: "A",
    range: "(92-100)",
    color: "#00a65a",
    textColor: "white",
    width: "70%"
  },
  {
    label: "B",
    range: "(81-91)",
    color: "#4cb848",
    textColor: "white",
    width: "76%"
  },
  {
    label: "C",
    range: "(69-80)",
    color: "#c6d21f",
    textColor: "#1f2933",
    width: "82%"
  },
  {
    label: "D",
    range: "(55-68)",
    color: "#f5b036",
    textColor: "#1f2933",
    width: "88%"
  },
  {
    label: "E",
    range: "(39-54)",
    color: "#f28b2c",
    textColor: "white",
    width: "94%"
  },
  {
    label: "F",
    range: "(21-38)",
    color: "#e15326",
    textColor: "white",
    width: "98%"
  },
  {
    label: "G",
    range: "(1-20)",
    color: "#c32026",
    textColor: "white",
    width: "100%"
  }
]

const normalizeRating = (rating?: string | null) =>
  rating?.toUpperCase().trim() || undefined

const formatScore = (value?: string | number | null) => {
  if (value === null || value === undefined || value === "") return undefined
  return typeof value === "number" ? Math.round(value).toString() : `${value}`
}

interface EpcRatingChartProps {
  currentRating?: string | null
  currentScore?: string | number | null
  potentialRating?: string | null
  potentialScore?: string | number | null
  inspectionDate?: string
}

const EpcRatingChart = ({
  currentRating,
  currentScore,
  potentialRating,
  potentialScore,
  inspectionDate
}: EpcRatingChartProps) => {
  const normalizedCurrent = normalizeRating(currentRating)
  const normalizedPotential = normalizeRating(potentialRating)
  const currentScoreText = formatScore(currentScore)
  const potentialScoreText = formatScore(potentialScore)
  const gridTemplateColumns = "minmax(140px, 200px) 110px 110px"

  const renderIndicator = (
    label: string | undefined,
    scoreText: string | undefined,
    color: string
  ) => {
    if (!label && !scoreText) return null
    return (
      <Box
        bg={color}
        color="white"
        px={4}
        py={1}
        fontSize="xs"
        fontWeight="semibold"
        borderRadius="sm"
        style={{ clipPath: EPC_ARROW_SHAPE_LEFT }}
      >
        {scoreText && label ? `${scoreText} ${label}` : label || scoreText}
      </Box>
    )
  }

  return (
    <VStack gap={1.5} align="stretch">
      <Box
        display="grid"
        gridTemplateColumns={gridTemplateColumns}
        gap={1.5}
        fontSize="xs"
        color="gray.600"
        fontWeight="semibold"
      >
        <Text textAlign="left" fontWeight="light" color="gray.500">
          RATINGS
        </Text>
        <Text textAlign="center" fontWeight="light" color="gray.500">
          CURRENT
        </Text>
        <Text textAlign="center" fontWeight="light" color="gray.500">
          POTENTIAL
        </Text>
      </Box>
      {EPC_SCALE.map((band) => {
        const isCurrent = normalizedCurrent === band.label
        const isPotential = normalizedPotential === band.label
        return (
          <Box
            key={band.label}
            display="grid"
            gridTemplateColumns={gridTemplateColumns}
            alignItems="center"
            gap={1.5}
          >
            <Box
              bg={band.color}
              color={band.textColor}
              px={3}
              py={0.75}
              fontWeight="bold"
              borderRadius="md"
              justifySelf="start"
              style={{ clipPath: EPC_ARROW_SHAPE_RIGHT, width: band.width }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={2}
              >
                <Text fontSize="xs" fontWeight="semibold">
                  {band.range}
                </Text>
                <Text fontSize="md" fontWeight="extrabold" pr={2}>
                  {band.label}
                </Text>
              </Box>
            </Box>
            <Box
              minH="22px"
              display="flex"
              justifyContent="center"
              alignItems="flex-end"
              pl={3}
            >
              {isCurrent &&
                renderIndicator(band.label, currentScoreText, band.color)}
            </Box>
            <Box minH="22px" display="flex" justifyContent="center" pl={3}>
              {isPotential &&
                renderIndicator(band.label, potentialScoreText, band.color)}
            </Box>
          </Box>
        )
      })}
      {inspectionDate && (
        <Box pt={1}>
          <Text fontSize="xs" color="gray.600" mb={1}>
            EPC Inspection Date
          </Text>
          <Text fontSize="sm">{inspectionDate}</Text>
        </Box>
      )}
    </VStack>
  )
}

export default EpcRatingChart
